/* eslint-disable no-console */
import {SerializedError} from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import axios, {isAxiosError} from 'axios';
import {Duration, Effect, Layer, Schedule} from 'effect';
import {InterruptedException} from 'effect/Cause';

import {LoaderOptions} from 'modules/Tasks/components/Gantt/utils/load';
import {serializeError} from 'store/utils/serializeError';

import {EffectError} from '../errors';

export const calculateBatchParameters = (total: number, configuredBatchSize?: number) => {
  // Handle negative numbers and ensure total is non-negative
  const safeTotal = Math.max(0, total);

  // Handle invalid configuredBatchSize
  const safeBatchSize = configuredBatchSize && configuredBatchSize > 0 ? configuredBatchSize : 1_000;

  // For very small datasets, just load everything in one batch
  if (safeTotal <= 100) {
    return {
      batchSize: safeTotal,
      batchCount: safeTotal === 0 ? 1 : Math.ceil(safeTotal / safeTotal),
      concurrency: 1,
    };
  }

  // For small datasets (101-500 items), use 2 batches
  if (safeTotal <= 500) {
    const batchSize = Math.ceil(safeTotal / 2);
    return {
      batchSize,
      batchCount: Math.ceil(safeTotal / batchSize),
      concurrency: 2,
    };
  }

  // For medium datasets (501-750), use 3 batches
  if (safeTotal <= 750) {
    const batchSize = Math.ceil(safeTotal / 3);
    return {
      batchSize,
      batchCount: Math.ceil(safeTotal / batchSize),
      concurrency: 3,
    };
  }

  // For larger datasets (751-2500), use 5 batches
  if (safeTotal <= 2_500) {
    const batchSize = Math.ceil(safeTotal / 5);
    return {
      batchSize,
      batchCount: Math.ceil(safeTotal / batchSize),
      concurrency: 5,
    };
  }

  // For very large datasets (2500-5000), use 10 batches
  if (safeTotal <= 5_000) {
    const batchSize = Math.ceil(safeTotal / 10);
    return {
      batchSize,
      batchCount: Math.ceil(safeTotal / batchSize),
      concurrency: 10,
    };
  }

  const IDEAL_BATCH_SIZE = safeBatchSize;
  const MAX_CONCURRENT_REQUESTS = 10;
  // Calculate number of batches needed with ideal batch size
  const batchCount = Math.ceil(safeTotal / IDEAL_BATCH_SIZE);
  // Adjust concurrency based on batch count
  const concurrency = Math.min(batchCount, MAX_CONCURRENT_REQUESTS);

  return {
    batchSize: IDEAL_BATCH_SIZE,
    batchCount,
    concurrency,
  };
};

export const formatTime = (ms: number) => {
  const MILLISECONDS_IN_SECOND = 1_000;
  const MILLISECONDS_IN_MINUTE = 60_000;
  const DEFAULT_TIME = '0ms';
  const DECIMAL_PLACES = 1;
  const INTEGER_PLACES = 0;

  if (!ms || Number.isNaN(ms)) {
    return DEFAULT_TIME;
  }

  // Less than a second
  if (ms < MILLISECONDS_IN_SECOND) {
    return `${ms.toFixed(INTEGER_PLACES)}ms`;
  }

  // Less than a minute
  if (ms < MILLISECONDS_IN_MINUTE) {
    return `${(ms / MILLISECONDS_IN_SECOND).toFixed(DECIMAL_PLACES)}s`;
  }

  // Minutes and seconds
  const minutes = Math.floor(ms / MILLISECONDS_IN_MINUTE);
  const seconds = ((ms % MILLISECONDS_IN_MINUTE) / MILLISECONDS_IN_SECOND).toFixed(DECIMAL_PLACES);
  return `${minutes}m ${seconds}s`;
};

export async function runLoaderEffect<T extends InterruptedException | unknown[] | void, E extends EffectError, R>(
  program: Effect.Effect<T, E, R>,
  provider: Layer.Layer<R, never, never>,
  options: LoaderOptions,
): Promise<T | undefined> {
  if (!options.name || !options.projectId) {
    throw new Error('Missing required options: name and projectId are required');
  }

  const runnable = program.pipe(
    Effect.provide(provider),
    Effect.catchAll((error: E) => {
      if (error instanceof InterruptedException) {
        return Effect.succeed(new InterruptedException('InterruptedException') as T);
      }
      return Effect.fail(error);
    }),
  );

  try {
    const result = await Effect.runPromise(runnable);
    return result;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        projectId: options.projectId,
        name: options.name,
      },
      extra: options.extra,
    });
  }
}

export const createProjectLoader = <T>(
  projectId: string,
  loaderFn: Effect.Effect<T, EffectError, never>,
): Effect.Effect<T, EffectError, never> => {
  return Effect.gen(function* (_) {
    // eslint-disable-next-line import/no-named-as-default-member
    const cancelTokenSource = axios.CancelToken.source();

    return yield* _(
      loaderFn.pipe(
        Effect.ensuring(
          Effect.sync(() => {
            cancelTokenSource.cancel('Project changed or operation completed');
          }),
        ),
        Effect.withSpan('projectLoader', {
          attributes: {
            projectId,
            cancelToken: cancelTokenSource.token,
          },
        }),
      ),
    );
  });
};

export type ApiError = {
  type: 'network' | 'validation' | 'server';
  message: string;
  statusCode?: number;
  serialized: SerializedError;
  original?: unknown;
};

type RetryConfig = {
  initialDelay?: number;
  maxAttempts?: number;
  backoffFactor?: number;
  /**
   * @property {string} context - Used to identify the source of the error in Sentry
   */
  context: string;
};

const isNetworkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;

  const networkErrorCodes = [
    'ECONNABORTED',
    'ERR_NETWORK',
    'ERR_CONNECTION_REFUSED',
    'ETIMEDOUT',
    'ERR_NETWORK_ACCESS_DENIED',
  ];

  return Boolean((error.code && networkErrorCodes.includes(error.code)) || !error.response);
};

const getErrorType = (error: unknown): ApiError['type'] => {
  if (!isAxiosError(error)) {
    return 'server';
  }

  if (isNetworkError(error)) {
    return 'network';
  }

  const status = error.response?.status;
  if (!status) {
    return 'server';
  }

  switch (true) {
    case status === 422:
    case status === 400:
      return 'validation';
    case status >= 401 && status <= 499:
      return 'server';
    case status >= 500:
      return 'server';
    default:
      return 'server';
  }
};

const shouldRetryApiError = (error: ApiError): boolean => {
  // always retry network errors
  if (error.type === 'network') {
    return true;
  }

  // retry server errors only if they're > 5xx
  if (error.type === 'server' && isAxiosError(error.original)) {
    const statusCode = error.original.response?.status ?? 500;
    const shouldRetry = statusCode > 500;
    return shouldRetry;
  }

  return false;
};

export const withRetry = <T>(
  apiCall: () => Promise<T>,
  {initialDelay = 1, maxAttempts = 3, backoffFactor = 1.5, context}: RetryConfig,
): Effect.Effect<T, ApiError, never> => {
  return Effect.retry(
    Effect.tryPromise({
      try: apiCall,
      catch: (error: unknown): ApiError => {
        const type = getErrorType(error);
        let message = 'Unknown error';
        let statusCode: number | undefined;

        if (isAxiosError(error)) {
          message = error.message;
          statusCode = error.response?.status;
        } else if (error instanceof Error) {
          message = error.message;
        }

        return {
          type,
          message,
          statusCode,
          serialized: serializeError(error),
          original: error,
        };
      },
    }),
    Schedule.exponential(Duration.seconds(initialDelay), backoffFactor).pipe(
      Schedule.intersect(Schedule.recurs(maxAttempts)),
      Schedule.whileInput(shouldRetryApiError),
    ),
  ).pipe(
    Effect.catchAll((error) => {
      Sentry.captureException(error.serialized, {
        tags: {context, errorType: error.type},
      });
      return Effect.fail(error);
    }),
  );
};
